import type { FileWithMeta } from '@risksmart-app/components/src/file/types';
import _ from 'lodash';

export const assignPathToFiles = (files: File[], path: string) =>
  files.map((f) => _.assignIn(f, { meta: { path } }));

// Adds the path (form field) the file belongs to as metadata.
// We have to use assignIn because the File type is protected.
export const enrichFilesWithPathMetadata = (files: {
  [key: string]: File[];
}): (File & { meta?: { [key: string]: string } })[] => {
  const filesWithMeta: (File & { meta?: { [key: string]: string } })[] = [];
  _.keys(files).forEach((k) => {
    filesWithMeta.push(...assignPathToFiles(files[k], k));
  });

  return filesWithMeta;
};

export type OriginalFiles =
  | {
      file?:
        | {
            Id: string;
            Meta?: { path?: string } | null | undefined;
          }
        | null
        | undefined;
    }[]
  | undefined;

/**
 * Given the original files and the updated files, this function filters out the files that have been deleted.
 * If there are no updated files, it returns all original files that have a path.
 * If there are updated files, if the file's path is not present, it is not considered deleted
 * If the file's path is present, if the file is not present in the updated files for the given path, it is considered deleted.
 * @param originalFiles List of files returned from response
 * @param updatedFiles List of updated files
 * @returns List of files that have not been deleted (i.e selected files)
 */
export const removeDeletedFiles = (
  originalFiles: OriginalFiles,
  updatedFiles: {
    [key: string]: FileWithMeta[];
  }
) => {
  if (!originalFiles) {
    return [];
  }

  return originalFiles
    .filter((f) => {
      if (!f?.file?.Meta?.path) {
        return false;
      }

      if (!updatedFiles?.[f.file.Meta.path]) {
        return true;
      }

      return updatedFiles[f.file.Meta.path].some(
        (ff) => ff.file.Id === f.file?.Id
      );
    })
    .map((f) => f.file);
};
